import React, { useEffect, useState } from "react";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
  Box,
  Typography,
} from "@mui/material";
import UpdateUserBtn from "./UpdateUserBtn";
import DeleteUserBtn from "./DeleteUserBtn";

const UsersTable = ({ data, fetchUsers }) => {
  const [users, setUsers] = useState(data);

  useEffect(() => {
    setUsers(data);
  }, [data]);

  const handleUserUpdate = async (updatedUser) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) => (user.id === updatedUser.id ? updatedUser : user))
    );

    await fetchUsers();
  };

  // Role color mapping
  const getRoleColor = (role) => {
    switch(role) {
      case 'super-admin':
        return {
          bgcolor: 'rgba(139, 0, 139, 0.1)',  // Purple shade
          color: '#8B008B',                   // Dark purple
          border: '1px solid #8B008B'
        };
      case 'data-admin':
        return {
          bgcolor: 'rgba(0, 100, 0, 0.1)',    // Dark green shade
          color: '#006400',                   // Dark green
          border: '1px solid #006400'
        };
      case 'website-admin':
        return {
          bgcolor: 'rgba(0, 0, 139, 0.1)',    // Dark blue shade
          color: '#00008B',                   // Dark blue
          border: '1px solid #00008B'
        };
      default:
        return {
          bgcolor: 'rgba(128, 128, 128, 0.1)', // Gray shade
          color: '#808080',                    // Gray
          border: '1px solid #808080'
        };
    }
  };

  return (
    <Container maxWidth="lg" sx={{ marginTop: 1, marginBottom: 4 }}>
      <Paper elevation={6} sx={{ 
        borderRadius: 3, 
        overflow: "hidden",
        border: "none",
        boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)"
      }}>
        {/* Table Title */}
        <Box
          sx={{
            background: "#1976d2",
            color: "white",
            padding: 2.5,
            textAlign: "center",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ 
            textShadow: "0 2px 4px rgba(0,0,0,0.2)",
            letterSpacing: "0.5px"
          }}>
            Users Management
          </Typography>
        </Box>

        <TableContainer>
          <MuiTable sx={{
            minWidth: 650,
            "& .MuiTableCell-root": {
              padding: "14px 16px"
            }
          }}>
            {/* Table Head */}
            <TableHead>
              <TableRow
                sx={{
                  background: "linear-gradient(135deg, #f0effc 0%, #e0e0f8 100%)",
                  "& th": { 
                    color: "#1976D2",
                    fontWeight: "bold",
                    fontSize: "0.95rem",
                    letterSpacing: "0.5px"
                  },
                }}
              >
                <TableCell>ID</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>

            {/* Table Body with Role Colors */}
            <TableBody>
              {users.length > 0 ? (
                users.map((user) => (
                  <TableRow 
                    key={user.id}
                    sx={{ 
                      '&:nth-of-type(even)': { 
                        backgroundColor: 'rgba(240, 239, 252, 0.3)' 
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(74, 144, 226, 0.05)'
                      },
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{user.id}</TableCell>
                    <TableCell>
                      <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        ...getRoleColor(user.role),
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'capitalize',
                        letterSpacing: '0.3px'
                      }}>
                        {user.role}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{user.username}</TableCell>
                    <TableCell sx={{ color: '#555' }}>{user.email}</TableCell>
                    <TableCell align="center" sx={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 1
                    }}>
                      <UpdateUserBtn user={user} onUserUpdate={handleUserUpdate} />
                      <DeleteUserBtn userId={user.id} onUserDelete={fetchUsers} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ 
                    py: 4,
                    color: '#777',
                    fontStyle: 'italic'
                  }}>
                    No users found. Add a new user to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </MuiTable>
        </TableContainer> 
      </Paper>
    </Container>
  );
};

export default UsersTable;